// src/features/dashboard/components/charts/UserInteractionChart/UserInteractionChart.jsx
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { Box, FormControl, MenuItem, Select } from "@mui/material";
import HourglassFullRoundedIcon from "@mui/icons-material/HourglassFullRounded";

import BarChart from "../../../../../components/Charts/BarChart/BarChart";
import ChartWrapper from "../../../../../components/common/ChartWrapper/ChartWrapper";
import ChartHeader from "../../../../../components/common/ChartHeader/ChartHeader";
import {
  transformUserInteractionData,
  buildSessionChartData,
} from "../../../utils/chartTransformers";

import "../dashboardCharts.scss";

const USER_FILTER_OPTIONS = ["Top 5", "All"];

const UserInteractionChart = () => {
  const { userInteraction, userInteractionFetching } = useSelector(
    (s) => s.dashboard,
  );
  const [userFilter, setUserFilter] = useState("Top 5");

  const { labels, sessionCounts, avgDurations } = useMemo(
    () => transformUserInteractionData(userInteraction),
    [userInteraction],
  );

  const filteredData = useMemo(() => {
    const combined = labels.map((label, i) => ({
      label,
      sessionCount: sessionCounts[i],
      avgDuration: avgDurations[i],
    }));

    const sorted = [...combined].sort(
      (a, b) => b.sessionCount - a.sessionCount,
    );
    const displayed = userFilter === "Top 5" ? sorted.slice(0, 5) : sorted;

    return buildSessionChartData(
      displayed.map((d) => d.label),
      displayed.map((d) => d.sessionCount),
      displayed.map((d) => d.avgDuration),
    );
  }, [labels, sessionCounts, avgDurations, userFilter]);

  const isEmpty = filteredData.labels.length === 0;

  const filterSelect = (
    <Select
      value={userFilter}
      onChange={(e) => setUserFilter(e.target.value)}
      sx={{
        height: 36,
        width: 145,
        fontSize: "14px",
        ".MuiOutlinedInput-notchedOutline": {
          border: "1px solid #e5e5ea !important",
        },
        "&:hover .MuiOutlinedInput-notchedOutline": {
          borderColor: "#1976d2 !important",
        },
      }}
    >
      {USER_FILTER_OPTIONS.map((opt) => (
        <MenuItem key={opt} value={opt}>
          {opt}
        </MenuItem>
      ))}
    </Select>
  );

  return (
    <Box className="chart-card animate-card">
      <ChartHeader
        icon={HourglassFullRoundedIcon}
        title="User Interaction Analysis"
        action={<FormControl>{filterSelect}</FormControl>}
      />
      <ChartWrapper
        isLoading={userInteractionFetching}
        isEmpty={isEmpty}
        height="400px"
      >
        <Box height="400px" width="100%">
          <BarChart data={filteredData} enableSorting enableDualAxis />
        </Box>
      </ChartWrapper>
    </Box>
  );
};

export default UserInteractionChart;
